import * as React from "react";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import Box from "@amzn/awsui-components-react/polaris/box";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Button from "@amzn/awsui-components-react/polaris/button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/rootReducer";
import {
  clearOclModal,
  OneClickLaunchModalType,
} from "./oneClickLaunchModalConnector";
import {
  addRemovedAsinMarketplaces,
  batchRemoveWeblabFromAsinMarketplaces,
  getSelectedItemKey,
  publishIndexSuppressedAnd3pse,
  publishLaunchDates,
  resetLaunchDates,
  setAsinMarketplaces,
  setSelectedAsinMarketplaces,
} from "./oneClickLaunchTableConnector";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../server/asin-central/model";
import { Dispatch, useState } from "react";
import { LaunchSummaryComponent } from "./SummaryView";
import { Popover, StatusIndicator } from "@amzn/awsui-components-react";
import { trackButtonClick } from "../telemetry/telemetry";
import { Input } from "@amzn/awsui-components-react";
import { NonCancelableCustomEvent } from "@amzn/awsui-components-react/polaris";
import { BaseChangeDetail } from "@amzn/awsui-components-react/uxdg/input/interfaces";

export default () => {
  const dispatch = useDispatch();
  const { isOpen, type, title, message } = useSelector(
    (state: RootState) => state.oneClickLaunchModal
  );
  const {
    asinMarketplaces,
    selectedAsinMarketplacesKeys,
    selectedAsinMarketplacesContainsRestrictedItems,
    selectedAsinMarketplacesContainsLiveItems,
  } = useSelector((state: RootState) => state.asinOneClickLaunchTableControl);
  const { indexSuppressedSelectedMap, _3pseSelectedMap } = useSelector(
    (state: RootState) => state.oneClickLaunch
  );

  const [confirmText, setConfirmText] = useState("");

  const handleChange = (event: NonCancelableCustomEvent<BaseChangeDetail>) => {
    const value = event.detail.value;
    setConfirmText(value);
  };

  function RemoveAsinsFromView(
    asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[],
    selectedAsinMarketplacesKeys: string[],
    dispatch: Dispatch<any>
  ) {
    // Using a set for instant lookups.
    const set = new Set(selectedAsinMarketplacesKeys);
    dispatch(
      setAsinMarketplaces(
        asinMarketplaces.filter((item) => !set.has(getSelectedItemKey(item)))
      )
    );
    dispatch(addRemovedAsinMarketplaces(selectedAsinMarketplacesKeys));
    dispatch(setSelectedAsinMarketplaces([]));
    dispatch(clearOclModal());
  }

  function getModalButtons(
    type: OneClickLaunchModalType,
    asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[],
    selectedAsinMarketplacesKeys: string[],
    indexSuppressedSelectedMap: { [key: string]: boolean },
    _3pseSelectedMap: { [key: string]: boolean },
    selectedAsinMarketplacesContainsRestrictedItems: boolean,
    selectedAsinMarketplacesContainsLiveItems: boolean,
    confirmTyped: boolean
  ) {
    const dispatch = useDispatch();
    switch (type) {
      case "RemoveAsins":
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="ocl-cancel-remove-button"
              variant="link"
              onClick={() => {
                trackButtonClick(
                  "oneClickLaunchTable",
                  "CancelRemoveAsins",
                  {}
                );
                dispatch(clearOclModal());
                setConfirmText("");
              }}
            >
              Cancel
            </Button>
            <Button
              data-testid="ocl-remove-button"
              variant="primary"
              onClick={() => {
                trackButtonClick("oneClickLaunchTable", "RemoveAsins", {});
                RemoveAsinsFromView(
                  asinMarketplaces,
                  selectedAsinMarketplacesKeys,
                  dispatch
                );
                dispatch(clearOclModal());
                setConfirmText("");
              }}
            >
              Remove
            </Button>
            {(selectedAsinMarketplacesContainsRestrictedItems ||
              selectedAsinMarketplacesContainsLiveItems) && (
              <Popover
                dismissButton={true}
                header=""
                position="top"
                size="medium"
                triggerType="custom"
                content={
                  <span>
                    You cannot reset dates on Restricted or Live/Missing
                    liveness ASINs.
                    <br />
                  </span>
                }
              >
                <StatusIndicator type="info" />
              </Popover>
            )}
            <Button
              data-testid="ocl-reset-and-remove-button"
              variant="primary"
              disabled={
                selectedAsinMarketplacesContainsRestrictedItems ||
                selectedAsinMarketplacesContainsLiveItems ||
                !confirmTyped
              }
              onClick={() => {
                if (confirmTyped === true) {
                  trackButtonClick(
                    "oneClickLaunchTable",
                    "ResetAndRemoveAsins",
                    {}
                  );
                  dispatch(
                    resetLaunchDates(
                      asinMarketplaces,
                      selectedAsinMarketplacesKeys
                    )
                  );
                  RemoveAsinsFromView(
                    asinMarketplaces,
                    selectedAsinMarketplacesKeys,
                    dispatch
                  );
                  dispatch(clearOclModal());
                  setConfirmText("");
                }
              }}
            >
              Reset And Remove
            </Button>
          </SpaceBetween>
        );
      case "PublishLaunchDates":
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="ocl-cancel-publish-dates-button"
              variant="link"
              onClick={() => {
                trackButtonClick(
                  "oneClickLaunchTable",
                  "CancelPublishLaunchDates",
                  {}
                );
                dispatch(clearOclModal());
              }}
            >
              Cancel
            </Button>
            <Button
              data-testid="ocl-publish-dates-button"
              variant="primary"
              onClick={() => {
                trackButtonClick(
                  "oneClickLaunchTable",
                  "PublishLaunchDates",
                  {}
                );
                dispatch(publishLaunchDates(asinMarketplaces));
                dispatch(clearOclModal());
              }}
            >
              Publish
            </Button>
          </SpaceBetween>
        );
      case "PublishLaunchAttributesRemoveWeblab":
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              data-testid="ocl-cancel-publish-attributes-button"
              variant="link"
              onClick={() => {
                trackButtonClick(
                  "oneClickLaunchTable",
                  "CancelPublishLaunchAndRemoveWeblab",
                  {}
                );
                dispatch(clearOclModal());
              }}
            >
              Cancel
            </Button>
            <Button
              data-testid="ocl-publish-attributes-button"
              onClick={() => {
                trackButtonClick(
                  "oneClickLaunchTable",
                  "PublishLaunchAndRemoveWeblab",
                  {}
                );
                dispatch(
                  publishIndexSuppressedAnd3pse(
                    asinMarketplaces,
                    indexSuppressedSelectedMap,
                    _3pseSelectedMap
                  )
                );
                dispatch(
                  batchRemoveWeblabFromAsinMarketplaces(asinMarketplaces)
                );
                dispatch(clearOclModal());
              }}
              variant="primary"
            >
              Publish
            </Button>
          </SpaceBetween>
        );
    }
  }

  function getModalBody(
    type: OneClickLaunchModalType,
    asinMarketplaces: (FullAsinWithAudits | RestrictedAsin)[],
    selectedAsinMarketplacesKeys: string[]
  ) {
    const selectedSet = new Set(selectedAsinMarketplacesKeys);
    switch (type) {
      case "RemoveAsins":
        return (
          <LaunchSummaryComponent
            asinMarketplaces={asinMarketplaces.filter((item) =>
              selectedSet.has(getSelectedItemKey(item))
            )}
            title={"ASIN Removal Summary"}
            headerSize={"h2"}
          />
        );
      case "PublishLaunchDates":
        return (
          <LaunchSummaryComponent
            asinMarketplaces={asinMarketplaces}
            title={"Launch Scope Summary"}
            headerSize={"h2"}
          />
        );
      case "PublishLaunchAttributesRemoveWeblab":
        return (
          <LaunchSummaryComponent
            asinMarketplaces={asinMarketplaces}
            title={"Launch Scope Summary"}
            headerSize={"h2"}
          />
        );
    }
  }
  function getConfirmationBox(type: OneClickLaunchModalType) {
    switch (type) {
      case "RemoveAsins":
        return (
          <Box>
            To "Reset and Remove" please type in <b>confirm</b> here
            <Input
              data-testid="ocl-confirm-input"
              value={confirmText}
              type="text"
              onChange={handleChange}
            ></Input>
          </Box>
        );
    }
  }

  return (
    <Modal
      data-testid="one-click-launch-modal"
      onDismiss={() => {
        dispatch(clearOclModal());
        setConfirmText("");
      }}
      visible={isOpen}
      size={"large"}
      footer={
        <Box float="right">
          {getModalButtons(
            type,
            asinMarketplaces,
            selectedAsinMarketplacesKeys,
            indexSuppressedSelectedMap,
            _3pseSelectedMap,
            selectedAsinMarketplacesContainsRestrictedItems,
            selectedAsinMarketplacesContainsLiveItems,
            confirmText == "confirm"
          )}
        </Box>
      }
      header={title}
    >
      {message}
      {selectedAsinMarketplacesContainsRestrictedItems && (
        <div>
          <br />
          <span>
            NOTE: Removing any restricted ASINs will remove all restricted ASINs
          </span>
        </div>
      )}
      <SpaceBetween direction="vertical" size="xs">
        {getModalBody(type, asinMarketplaces, selectedAsinMarketplacesKeys)}
        {getConfirmationBox(type)}
      </SpaceBetween>
    </Modal>
  );
};
